import ConversationAPI from '../../api/conversations';
import types from '../mutation-types';

export const state = {
  allCount: 0,
  inboxes: {},
  labels: {},
  teams: {},
  mentionsCount: 0,
  participatingCount: 0,
  unattendedCount: 0,
  folders: {},
  // Not an unread count: the size of the AI Agent "Handling" queue (pending
  // conversations visible to the user), delivered on the same payload.
  aiHandlingCount: 0,
  // Unread tickets per tickets-sidebar view, keyed by TICKET_VIEWS key
  // ('all', 'unassigned', 'my_open', 'open', 'pending', 'on_hold', 'solved',
  // 'closed', 'archive', 'spam', 'trash'). Empty unless the tickets feature is
  // enabled for the account.
  ticketViews: {},
  // Every ticket per view, read or not, over the same keys. A worked queue is
  // sized by what is in it, so this - not the unread tally - is what the
  // Tickets sidebar badges.
  ticketViewTotals: {},
};

const normalizeCount = count => {
  const parsedCount = Number(count);
  return Number.isFinite(parsedCount) && parsedCount > 0 ? parsedCount : 0;
};

const normalizeCounts = counts => {
  return Object.entries(counts || {}).reduce((result, [id, count]) => {
    const parsedCount = normalizeCount(count);
    if (parsedCount > 0) {
      result[String(id)] = parsedCount;
    }

    return result;
  }, {});
};

export const getters = {
  getAllUnreadCount($state) {
    return $state.allCount;
  },
  getInboxUnreadCount: $state => inboxId => {
    return $state.inboxes[String(inboxId)] || 0;
  },
  getLabelUnreadCount: $state => labelId => {
    return $state.labels[String(labelId)] || 0;
  },
  getTeamUnreadCount: $state => teamId => {
    return $state.teams[String(teamId)] || 0;
  },
  getMentionsUnreadCount($state) {
    return $state.mentionsCount;
  },
  getParticipatingUnreadCount($state) {
    return $state.participatingCount;
  },
  getUnattendedUnreadCount($state) {
    return $state.unattendedCount;
  },
  getAiHandlingCount($state) {
    return $state.aiHandlingCount;
  },
  getFolderUnreadCount: $state => folderId => {
    return $state.folders[String(folderId)] || 0;
  },
  getInboxUnreadCounts($state) {
    return $state.inboxes;
  },
  getLabelUnreadCounts($state) {
    return $state.labels;
  },
  getTeamUnreadCounts($state) {
    return $state.teams;
  },
  getFolderUnreadCounts($state) {
    return $state.folders;
  },
  // The Tickets group header: unread tickets in the `all` view, which is every
  // ticket that has not been shelved into archive / spam / trash.
  getTicketsUnreadCount($state) {
    return $state.ticketViews.all || 0;
  },
  getTicketViewUnreadCount: $state => viewKey => {
    return $state.ticketViews[String(viewKey)] || 0;
  },
  getTicketViewUnreadCounts($state) {
    return $state.ticketViews;
  },
  // The Tickets group header: every ticket in the `all` view, i.e. everything
  // that has not been shelved into archive / spam / trash.
  getTicketsTotalCount($state) {
    return $state.ticketViewTotals.all || 0;
  },
  getTicketViewTotalCount: $state => viewKey => {
    return $state.ticketViewTotals[String(viewKey)] || 0;
  },
  getTicketViewTotalCounts($state) {
    return $state.ticketViewTotals;
  },
};

export const actions = {
  get: async function getUnreadCounts({ commit }) {
    try {
      const response = await ConversationAPI.getUnreadCounts();
      commit(types.SET_CONVERSATION_UNREAD_COUNTS, response.data.payload);
    } catch (error) {
      // Ignore errors so the sidebar can continue rendering without badges.
    }
  },
  clear({ commit }) {
    commit(types.SET_CONVERSATION_UNREAD_COUNTS, {});
  },
};

export const mutations = {
  [types.SET_CONVERSATION_UNREAD_COUNTS]($state, payload = {}) {
    $state.allCount = normalizeCount(payload.all_count);
    $state.inboxes = normalizeCounts(payload.inboxes);
    $state.labels = normalizeCounts(payload.labels);
    $state.teams = normalizeCounts(payload.teams);
    $state.mentionsCount = normalizeCount(payload.mentions_count);
    $state.participatingCount = normalizeCount(payload.participating_count);
    $state.unattendedCount = normalizeCount(payload.unattended_count);
    $state.folders = normalizeCounts(payload.folders);
    $state.aiHandlingCount = normalizeCount(payload.ai_handling_count);
    $state.ticketViews = normalizeCounts(payload.ticket_views);
    $state.ticketViewTotals = normalizeCounts(payload.ticket_view_totals);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
